/**
 * Funciones auxiliares para el proceso de Notas de Credito/Debito de Facturas (E34/E33)
 */
const FuncionesProcesoNotasFactura = () => {
  
  /**
   * Genera la configuracion de campos para el formulario de notas NC/ND
   * @param {Array} codigosModificacion - Lista de codigos de modificacion DGII
   * @returns {Array} Configuracion de campos del formulario
   */
  function generarCamposFormulario(codigosModificacion = []) {
    return [
      // Seccion: Factura Original (solo lectura)
      { 
        id: "ncf_factura", 
        type: "text", 
        label: "NCF Factura", 
        placeholder: "NCF de la factura original", 
        seccion: "Factura Original",
        disabled: true 
      },
      { 
        id: "nombre_cliente", 
        type: "text", 
        label: "Cliente", 
        placeholder: "Nombre del cliente", 
        seccion: "Factura Original",
        disabled: true 
      },
      { 
        id: "monto_factura", 
        type: "text", 
        label: "Monto Factura", 
        placeholder: "Monto total de la factura", 
        seccion: "Factura Original",
        disabled: true 
      },

      // Seccion: Informacion de la Nota
      { 
        id: "codigo_modificacion", 
        type: "select", 
        label: "Razon de modificacion (DGII)", 
        placeholder: "Seleccione la razon", 
        seccion: "Informacion de la Nota",
        options: codigosModificacion.map(codigo => ({ 
          label: `${codigo.codigo} - ${codigo.descripcion}`, 
          value: codigo.codigo 
        })),
        required: true
      },
      { 
        id: "razon_modificacion", 
        type: "textarea", 
        label: "Descripcion adicional", 
        placeholder: "Descripcion detallada (max 90 caracteres)", 
        seccion: "Informacion de la Nota",
        maxLength: 90
      },

      // Seccion: Montos
      { 
        id: "monto", 
        type: "number", 
        label: "Subtotal", 
        placeholder: "Monto sin impuestos", 
        seccion: "Montos",
        required: true
      },
      { 
        id: "impuesto_total", 
        type: "number", 
        label: "ITBIS", 
        placeholder: "Total de impuestos", 
        seccion: "Montos",
        required: true
      },
      { 
        id: "monto_total", 
        type: "text", 
        label: "Total", 
        placeholder: "Monto total con impuestos", 
        seccion: "Montos",
        disabled: true 
      },

      // Seccion: Adicional
      { 
        id: "comentario", 
        type: "textarea", 
        label: "Comentario interno", 
        placeholder: "Notas internas (no se envia a DGII)", 
        seccion: "Adicional"
      }
    ];
  }

  /**
   * Genera las columnas para la tabla de detalles de la nota
   * @returns {Array} Configuracion de columnas
   */
  function generarColumnasDetalles() {
    return [
      { 
        id: "descripcion_producto", 
        label: "Descripcion", 
        type: "text",
        width: "30%"
      },
      { 
        id: "cantidad", 
        label: "Cantidad", 
        type: "number",
        width: "10%"
      },
      { 
        id: "precio", 
        label: "Precio", 
        type: "number",
        width: "15%"
      },
      { 
        id: "descuento", 
        label: "Descuento", 
        type: "number",
        width: "10%"
      },
      { 
        id: "itbis_tasa", 
        label: "% ITBIS", 
        type: "select",
        options: [
          { label: "18%", value: 18 },
          { label: "16%", value: 16 },
          { label: "0%", value: 0 }
        ],
        width: "10%"
      },
      { 
        id: "total", 
        label: "Total", 
        type: "number",
        disabled: true,
        width: "15%"
      }
    ];
  }

  /**
   * Codigos de modificacion DGII predefinidos
   * @returns {Array} Lista de codigos de modificacion
   */
  function obtenerCodigosModificacion() {
    return [
      { codigo: 1, descripcion: "Anula el NCF modificado", aplica_nc: true, aplica_nd: true },
      { codigo: 2, descripcion: "Corrige texto del comprobante", aplica_nc: true, aplica_nd: true },
      { codigo: 3, descripcion: "Corrige montos del NCF", aplica_nc: true, aplica_nd: true },
      { codigo: 4, descripcion: "Reemplazo NCF contingencia", aplica_nc: true, aplica_nd: true },
      { codigo: 5, descripcion: "Referencia Factura Consumo", aplica_nc: true, aplica_nd: false }
    ];
  }

  /**
   * Calcula el total de un detalle
   * @param {Object} detalle - Detalle de la nota
   * @returns {number} Total calculado
   */
  function calcularTotalDetalle(detalle) {
    const cantidad = parseFloat(detalle.cantidad) || 0;
    const precio = parseFloat(detalle.precio) || 0;
    const descuento = parseFloat(detalle.descuento) || 0;
    const itbisTasa = parseFloat(detalle.itbis_tasa) || 0;
    
    const subtotal = (cantidad * precio) - descuento;
    const itbis = subtotal * (itbisTasa / 100);
    
    return subtotal + itbis;
  }

  /**
   * Calcula los totales de la nota basado en los detalles
   * @param {Array} detalles - Lista de detalles de la nota
   * @returns {Object} Totales calculados { monto, impuesto_total, monto_total }
   */
  function calcularTotalesNota(detalles) {
    let monto = 0;
    let impuesto_total = 0;

    detalles.forEach(detalle => {
      const cantidad = parseFloat(detalle.cantidad) || 0;
      const precio = parseFloat(detalle.precio) || 0;
      const descuento = parseFloat(detalle.descuento) || 0;
      const itbisTasa = parseFloat(detalle.itbis_tasa) || 0;

      const subtotal = (cantidad * precio) - descuento;
      const itbis = subtotal * (itbisTasa / 100);

      monto += subtotal;
      impuesto_total += itbis;
    });

    return {
      monto: parseFloat(monto.toFixed(2)),
      impuesto_total: parseFloat(impuesto_total.toFixed(2)),
      monto_total: parseFloat((monto + impuesto_total).toFixed(2))
    };
  }

  return {
    generarCamposFormulario,
    generarColumnasDetalles,
    obtenerCodigosModificacion,
    calcularTotalDetalle,
    calcularTotalesNota
  };
};

export default FuncionesProcesoNotasFactura;

